import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, UserX, UserCheck, Trash2, KeyRound, Shield, Pen, Eye, Clock, ArrowUpDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MobileNav from '@/components/MobileNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToReset, setUserToReset] = useState(null);
  const [userToEditRoles, setUserToEditRoles] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '', role: 'performer' });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [sortBy, setSortBy] = useState('created');

  useEffect(() => {
    fetchUsers();
  }, [sortBy]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?sort_by=${sortBy}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('FAILED TO LOAD USERS');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.email.trim() || !formData.password.trim()) {
      toast.error('FILL IN ALL FIELDS');
      return;
    }

    setSubmitting(true);
    try {
      let email = formData.email.trim();
      if (!email.includes('@')) {
        email = `${email}@bnnz.net`;
      }
      
      const response = await axios.post(`${API}/admin/users`, {
        ...formData,
        email
      });
      setUsers(prev => [...prev, response.data]);
      toast.success('USER CREATED');
      setDialogOpen(false);
      setFormData({ email: '', password: '', role: 'performer' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'FAILED TO CREATE USER');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/suspend`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'suspended' } : u));
      toast.success('USER SUSPENDED');
    } catch (error) {
      toast.error('FAILED TO SUSPEND');
    }
  };

  const handleUnsuspend = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/unsuspend`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: 'active' } : u));
      toast.success('USER UNSUSPENDED');
    } catch (error) {
      toast.error('FAILED TO UNSUSPEND');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      toast.success('USER DELETED');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error('FAILED TO DELETE');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userToReset || !newPassword.trim()) {
      toast.error('ENTER A NEW PASSWORD');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('PASSWORD MUST BE AT LEAST 6 CHARACTERS');
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(`${API}/admin/users/${userToReset.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('PASSWORD RESET');
      setResetPasswordDialogOpen(false);
      setUserToReset(null);
      setNewPassword('');
    } catch (error) {
      toast.error('FAILED TO RESET PASSWORD');
    } finally {
      setResetting(false);
    }
  };

  const handleToggleRole = async (userId, role, add) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/roles`, {
        role: role,
        action: add ? 'add' : 'remove'
      });
      
      setUsers(prev => prev.map(u => {
        if (u.id !== userId) return u;
        const currentRoles = u.roles || [u.role] || [];
        let newRoles;
        if (add) {
          newRoles = [...new Set([...currentRoles, role])];
        } else {
          newRoles = currentRoles.filter(r => r !== role);
        }
        return { ...u, roles: newRoles };
      }));
      
      toast.success(`ROLE ${add ? 'ADDED' : 'REMOVED'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'FAILED TO UPDATE ROLES');
    }
  };

  const openRoleDialog = (user) => {
    setUserToEditRoles(user);
    setRoleDialogOpen(true);
  };

  const getUserRoles = (user) => {
    return user.roles || [user.role] || [];
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-zom-red text-zom-white';
      case 'writer': return 'bg-zom-blue text-zom-white';
      case 'performer': return 'bg-zom-yellow text-zom-black';
      case 'guest': return 'bg-zom-white text-zom-black';
      default: return 'bg-zom-black text-zom-white';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zom-blue flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-zom-yellow border-t-transparent animate-spin" />
      </div>
    );
  }

  const admins = users.filter(u => getUserRoles(u).includes('admin'));
  const writers = users.filter(u => getUserRoles(u).includes('writer') && !getUserRoles(u).includes('admin'));
  const performers = users.filter(u => getUserRoles(u).includes('performer') && !getUserRoles(u).includes('writer') && !getUserRoles(u).includes('admin'));
  const guests = users.filter(u => getUserRoles(u).includes('guest') && !getUserRoles(u).includes('performer'));

  const UserCard = ({ user }) => {
    const roles = getUserRoles(user);
    const isSuspended = user.status === 'suspended';
    const hasLoggedIn = user.has_logged_in;
    
    const formatLastLogin = (timestamp) => {
      if (!timestamp) return 'NIGDY';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      
      if (hours < 1) return 'TERAZ';
      if (hours < 24) return `${hours}H`;
      if (days < 7) return `${days}D`;
      return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
    };
    
    return (
      <div
        className={`bg-zom-black border-3 p-4 ${isSuspended ? 'border-zom-red/50 opacity-60' : 'border-zom-white'}`}
        data-testid={`user-${user.id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center font-heading border-2 border-zom-black ${
            isSuspended ? 'bg-zom-black text-zom-red' : 'bg-zom-yellow text-zom-black'
          }`}>
            {user.email[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-zom-white font-heading text-sm truncate">@{user.email?.split('@')[0].toUpperCase()}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {roles.map(role => (
                <span key={role} className={`text-[9px] px-1.5 py-0.5 font-heading ${getRoleColor(role)}`}>
                  {role.toUpperCase()}
                </span>
              ))}
              {isSuspended && (
                <span className="text-[9px] px-1.5 py-0.5 font-heading bg-zom-red text-zom-white">SUSPENDED</span>
              )}
              {!hasLoggedIn && (
                <span className="text-[9px] px-1.5 py-0.5 font-heading bg-gray-600 text-zom-white">NOWY</span>
              )}
            </div>
          </div>
          {/* Last login indicator */}
          <div className="text-right">
            <div className="flex items-center gap-1 text-zom-white/50">
              <Clock className="w-3 h-3" strokeWidth={2.5} />
              <span className="text-[9px] font-heading">{formatLastLogin(user.last_login_at)}</span>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zom-white/20">
          <button
            onClick={() => openRoleDialog(user)}
            className="p-2 text-zom-blue hover:text-zom-yellow transition-colors"
            title="Manage Roles"
          >
            <Shield className="w-5 h-5" strokeWidth={2.5} />
          </button>
          
          {isSuspended ? (
            <button
              onClick={() => handleUnsuspend(user.id)}
              className="p-2 text-zom-yellow hover:text-zom-white transition-colors"
              title="Unsuspend"
            >
              <UserCheck className="w-5 h-5" strokeWidth={2.5} />
            </button>
          ) : (
            <button
              onClick={() => handleSuspend(user.id)}
              className="p-2 text-zom-white/60 hover:text-zom-red transition-colors"
              title="Suspend"
            >
              <UserX className="w-5 h-5" strokeWidth={2.5} />
            </button>
          )}
          
          <button
            onClick={() => { setUserToReset(user); setResetPasswordDialogOpen(true); }}
            className="p-2 text-zom-white/60 hover:text-zom-yellow transition-colors"
            title="Reset Password"
          >
            <KeyRound className="w-5 h-5" strokeWidth={2.5} />
          </button>
          
          {!roles.includes('admin') && (
            <button
              onClick={() => { setUserToDelete(user); setDeleteDialogOpen(true); }}
              className="p-2 text-zom-white/60 hover:text-zom-red transition-colors ml-auto"
              title="Delete"
            >
              <Trash2 className="w-5 h-5" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-zom-blue pt-20 pb-24" data-testid="admin-users-page">
      {/* Sub header */}
      <div className="fixed top-10 left-0 right-0 z-30 bg-zom-black/90 border-b-2 border-zom-yellow">
        <div className="flex items-center justify-between px-4 py-2">
          <span className="font-heading text-zom-white text-sm tracking-wider">UŻYTKOWNICY</span>
          <div className="flex items-center gap-2">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-32 h-8 bg-zom-black border-2 border-zom-yellow text-zom-white font-heading text-[10px]">
                <ArrowUpDown className="w-3 h-3 mr-1" strokeWidth={2.5} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zom-black border-2 border-zom-yellow">
                <SelectItem value="created" className="font-heading text-xs">UTWORZENI</SelectItem>
                <SelectItem value="last_login" className="font-heading text-xs">OSTATNI LOGIN</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => setDialogOpen(true)}
              className="w-8 h-8 bg-zom-yellow text-zom-black flex items-center justify-center border-2 border-zom-black"
              data-testid="create-user-btn"
            >
              <Plus className="w-5 h-5" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 pt-12 space-y-6">
        {/* Admins */}
        {admins.length > 0 && (
          <section>
            <h2 className="text-xs text-zom-red font-heading mb-3 tracking-widest flex items-center gap-2">
              <Shield className="w-4 h-4" strokeWidth={2.5} />
              ADMINS ({admins.length})
            </h2>
            <div className="space-y-3">
              {admins.map(user => <UserCard key={user.id} user={user} />)}
            </div>
          </section>
        )}

        {/* Writers */}
        {writers.length > 0 && (
          <section>
            <h2 className="text-xs text-zom-blue font-heading mb-3 tracking-widest flex items-center gap-2">
              <Pen className="w-4 h-4" strokeWidth={2.5} />
              WRITERS ({writers.length})
            </h2>
            <div className="space-y-3">
              {writers.map(user => <UserCard key={user.id} user={user} />)}
            </div>
          </section>
        )}

        {/* Performers */}
        {performers.length > 0 && (
          <section>
            <h2 className="text-xs text-zom-yellow font-heading mb-3 tracking-widest">
              PERFORMERS ({performers.length})
            </h2>
            <div className="space-y-3">
              {performers.map(user => <UserCard key={user.id} user={user} />)}
            </div>
          </section>
        )}

        {/* Guests */}
        {guests.length > 0 && (
          <section>
            <h2 className="text-xs text-zom-white font-heading mb-3 tracking-widest flex items-center gap-2">
              <Eye className="w-4 h-4" strokeWidth={2.5} />
              GUESTS ({guests.length})
            </h2>
            <div className="space-y-3">
              {guests.map(user => <UserCard key={user.id} user={user} />)}
            </div>
          </section>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zom-black border-3 border-zom-yellow text-zom-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-zom-yellow tracking-wider">NOWY UŻYTKOWNIK</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-xs text-zom-yellow font-heading mb-2 tracking-wider">USERNAME</label>
              <input
                type="text"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="username"
                className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <label className="block text-xs text-zom-yellow font-heading mb-2 tracking-wider">PASSWORD</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="password"
                className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <label className="block text-xs text-zom-yellow font-heading mb-2 tracking-wider">ROLA</label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger className="bg-zom-black border-2 border-zom-white text-zom-white font-heading" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zom-black border-2 border-zom-white">
                  <SelectItem value="performer">PERFORMER</SelectItem>
                  <SelectItem value="writer">WRITER</SelectItem>
                  <SelectItem value="guest">GUEST</SelectItem>
                  <SelectItem value="admin">ADMIN</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 py-2 border-2 border-zom-white text-zom-white font-heading hover:bg-zom-white hover:text-zom-black transition-colors"
            >
              ANULUJ
            </button>
            <button
              onClick={handleCreateUser}
              disabled={submitting}
              className="px-4 py-2 bg-zom-yellow text-zom-black font-heading border-2 border-zom-black disabled:opacity-50"
              data-testid="confirm-create-user-btn"
            >
              {submitting ? 'TWORZENIE...' : 'UTWÓRZ'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Management Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="bg-zom-black border-3 border-zom-yellow text-zom-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-zom-yellow tracking-wider">
              ROLE: @{userToEditRoles?.email?.split('@')[0].toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {['performer', 'writer', 'guest'].map(role => {
              const hasRole = userToEditRoles && getUserRoles(userToEditRoles).includes(role);
              // Don't allow removing performer if it's the only role (besides guest)
              const userCurrentRoles = userToEditRoles ? getUserRoles(userToEditRoles) : [];
              const canRemove = role !== 'performer' || userCurrentRoles.filter(r => r !== 'guest').length > 1;
              
              return (
                <div key={role} className="flex items-center justify-between p-3 border-2 border-zom-white/30">
                  <div className="flex items-center gap-2">
                    {role === 'performer' && <UserCheck className="w-5 h-5 text-zom-yellow" strokeWidth={2.5} />}
                    {role === 'writer' && <Pen className="w-5 h-5 text-zom-blue" strokeWidth={2.5} />}
                    {role === 'guest' && <Eye className="w-5 h-5 text-zom-white" strokeWidth={2.5} />}
                    <span className="font-heading text-sm">{role.toUpperCase()}</span>
                  </div>
                  <button
                    onClick={() => handleToggleRole(userToEditRoles.id, role, !hasRole)}
                    disabled={hasRole && !canRemove}
                    className={`px-4 py-2 font-heading text-xs border-2 transition-colors ${
                      hasRole 
                        ? canRemove
                          ? 'bg-zom-red text-zom-white border-zom-black hover:bg-zom-red/80' 
                          : 'bg-zom-white/20 text-zom-white/40 border-zom-white/20 cursor-not-allowed'
                        : 'bg-zom-yellow text-zom-black border-zom-black hover:bg-zom-yellow/80'
                    }`}
                  >
                    {hasRole ? 'USUŃ' : 'DODAJ'}
                  </button>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <button
              onClick={() => setRoleDialogOpen(false)}
              className="px-4 py-2 bg-zom-white text-zom-black font-heading border-2 border-zom-black"
            >
              ZAMKNIJ
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zom-black border-3 border-zom-yellow text-zom-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zom-red">USUNĄĆ UŻYTKOWNIKA?</AlertDialogTitle>
            <AlertDialogDescription className="text-zom-white/70 font-body">
              Spowoduje to trwałe usunięcie @{userToDelete?.email?.split('@')[0]} wraz ze wszystkimi postami.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zom-black border-2 border-zom-white text-zom-white font-heading">
              ANULUJ
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-zom-red text-zom-white font-heading border-2 border-zom-black"
            >
              {deleting ? 'USUWANIE...' : 'USUŃ'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="bg-zom-black border-3 border-zom-yellow text-zom-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-zom-yellow">RESET HASŁA</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-zom-white/70 text-sm mb-4 font-body">
              Nowe hasło dla @{userToReset?.email?.split('@')[0]}
            </p>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 6 znaków"
              className="w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white font-body"
              data-testid="reset-password-input"
            />
          </div>

          <DialogFooter>
            <button
              onClick={() => setResetPasswordDialogOpen(false)}
              className="px-4 py-2 border-2 border-zom-white text-zom-white font-heading"
            >
              ANULUJ
            </button>
            <button
              onClick={handleResetPassword}
              disabled={resetting || newPassword.length < 6}
              className="px-4 py-2 bg-zom-yellow text-zom-black font-heading border-2 border-zom-black disabled:opacity-50"
            >
              {resetting ? 'RESETOWANIE...' : 'RESETUJ'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileNav />
    </div>
  );
};

export default AdminUsersPage;
